import type { FilePreview, FilePreviewColumn, HeaderCellBinding } from './types';
import { indexToColLetters } from './types';

export function parseA1(cell: string): { row: number; col: number } {
  const m = String(cell || '')
    .trim()
    .toUpperCase()
    .match(/^([A-Z]+)(\d+)$/);
  if (!m) throw new Error(`Ogiltig celladress: ${cell}`);
  return {
    col: (() => {
      let n = 0;
      for (const ch of m[1]) n = n * 26 + (ch.charCodeAt(0) - 64);
      return n - 1;
    })(),
    row: parseInt(m[2], 10) - 1,
  };
}

export function resolveHeaderCellValuesFromPreamble(
  preambleRows: string[][],
  bindings: HeaderCellBinding[] | undefined | null
): { values: Record<string, string>; errors: string[] } {
  const values: Record<string, string> = {};
  const errors: string[] = [];
  if (!bindings?.length) return { values, errors };

  for (const binding of bindings) {
    if (!binding?.cell || !binding?.asColumn) continue;
    try {
      const { row, col } = parseA1(binding.cell);
      const raw = String(preambleRows[row]?.[col] ?? '').trim();
      values[binding.asColumn] = raw;

      if (binding.split?.pattern && Array.isArray(binding.split.parts) && binding.split.parts.length > 0) {
        const re = new RegExp(binding.split.pattern);
        let match = raw.match(re);
        // Fallback: bara kundnummer utan namn (t.ex. "104216")
        if (!match && /^\d+$/.test(raw)) {
          match = Object.assign([raw, raw, ''], { index: 0, input: raw, groups: undefined }) as RegExpMatchArray;
        }
        if (!match) {
          errors.push(`Header ${binding.cell} ("${raw}") matchade inte split-mönstret`);
          for (const part of binding.split.parts) {
            if (part?.asColumn) values[part.asColumn] = '';
          }
          continue;
        }
        for (const part of binding.split.parts) {
          if (!part?.asColumn) continue;
          values[part.asColumn] = String(match[part.group] ?? '').trim();
        }
      }
    } catch (err: unknown) {
      errors.push(err instanceof Error ? err.message : `Kunde inte läsa header-cell ${binding.cell}`);
    }
  }

  return { values, errors };
}

/** Virtuella kolumnnamn som headerCells skapar (inkl. split-delar). */
export function headerBindingColumnNames(bindings: HeaderCellBinding[]): string[] {
  const names: string[] = [];
  for (const b of bindings) {
    if (b.asColumn) names.push(b.asColumn);
    for (const part of b.split?.parts || []) {
      if (part.asColumn) names.push(part.asColumn);
    }
  }
  return names;
}

export function headerBindingsToFieldPairs(
  bindings: HeaderCellBinding[]
): Array<{ targetField: string; sourceColumn: string }> {
  const out: Array<{ targetField: string; sourceColumn: string }> = [];
  for (const binding of bindings) {
    if (binding.targetField && binding.asColumn) {
      out.push({ targetField: binding.targetField, sourceColumn: binding.asColumn });
    }
    for (const part of binding.split?.parts || []) {
      if (part.targetField && part.asColumn) {
        out.push({ targetField: part.targetField, sourceColumn: part.asColumn });
      }
    }
  }
  return out;
}

/** Uppdatera preview med virtuella kolumner utifrån preamble + headerCells. */
export function applyHeaderCellsToPreview(
  preview: FilePreview,
  bindings: HeaderCellBinding[]
): FilePreview {
  const preambleRows = preview.preambleRows || [];
  const { values, errors } = resolveHeaderCellValuesFromPreamble(preambleRows, bindings);
  const nonVirtual = preview.columns.filter((c) => {
    if (preview.headerCellValues && c.name in preview.headerCellValues) return false;
    if (c.name.startsWith('Header ')) return false;
    return true;
  });

  const columns: FilePreviewColumn[] = [...nonVirtual];
  for (const [name, value] of Object.entries(values)) {
    columns.push({
      index: columns.length,
      name,
      originalName: name,
      sampleValues: [value],
      detectedType: 'string',
    });
  }

  const previewRows = (preview.previewRows || []).map((row) => {
    const next = { ...row };
    for (const key of Object.keys(preview.headerCellValues || {})) {
      delete next[key];
    }
    for (const key of Object.keys(next)) {
      if (key.startsWith('Header ')) delete next[key];
    }
    Object.assign(next, values);
    return next;
  });

  return {
    ...preview,
    columns,
    previewRows,
    headerCellValues: values,
    errors: errors.length ? errors : undefined,
  };
}

export function cellRef(rowIndex: number, colIndex: number): string {
  return `${indexToColLetters(colIndex)}${rowIndex + 1}`;
}

/** Max antal kolumner att visa i preamble-rutnätet. */
export function preambleColCount(preambleRows: string[][], minCols = 6): number {
  let max = minCols;
  for (const row of preambleRows) {
    if (row.length > max) max = row.length;
  }
  // Trim trailing empty columns but keep at least minCols useful width
  let lastNonEmpty = minCols - 1;
  for (const row of preambleRows) {
    for (let i = row.length - 1; i >= 0; i--) {
      if (String(row[i] ?? '').trim()) {
        lastNonEmpty = Math.max(lastNonEmpty, i);
        break;
      }
    }
  }
  return Math.max(minCols, lastNonEmpty + 1);
}
